import React, { useState, useEffect, useRef, Fragment } from 'react';
import { connect } from 'react-redux';
import { ToastsStore } from '@bnsf/bnsf-react-toast-messages';
import Select from 'react-select';
import SwitchYesModal from '../../../Modals/SwitchYesModal';
// import Checkbox from '../../../common/Checkbox';

import { TRACK_TYPES, TRACK_DELIMITER_REGEX } from '../../../../constants/winConstants'
import * as trackTypeUtil from '../../../../utils/trackTypeUtil';
import { fromArray } from '../../../../utils/selectUtil';
import { getControlPointsByLSMP, getTrackListByLSMP, applySwitchYes, getAuthorityType } from '../../../../services/trackWindow';

import ActionTypes from '../constants';

import './WinEditStep2.css';

const mp_regexp = /[a-z]+|[^a-z]+/gi;
const ARRAY_YARD_INDUSTRY = ['Yard', 'Industry'];

const toLSMP = (win) => {
    return {
        lineSegment: win.lineSegment,
        bgnMp: win.bmp,
        bgnMpSfx: win.bmpSuf,
        endMp: win.emp,
        endMpSfx: win.empSuf
    }
}

const isValidNumber = num => {
    return !(Number.isNaN(parseFloat(num)));
}

const isValidLSMP = (ls, bmp, emp) => {
    return ls && isValidNumber(bmp) && isValidNumber(emp);
}

const WinEditStep2 = (props) => {
    const refClicNums = useRef(null);
    const isTerminalWindow = props.data.winType === 'TW';

    const [ lineSegment, setLineSegment ] = useState(props.data.lineSegment);
    const [ bmp, setBmp ] = useState(props.data.bmp + props.data.bmpSuf);
    const [ emp, setEmp ] = useState(props.data.emp + props.data.empSuf);
    const [ bcp, setBcp ] = useState(props.data.bcp);
    const [ ecp, setEcp ] = useState(props.data.ecp);
    const [ switchYesInProgress, setSwitchYesInProgress ] = useState(false);
    const [ switchYesResponse, setSwitchYesResponse ] = useState(null);
    const [ showSwitchYesModal, setShowSwitchYesModal ] = useState(false);
    const [ bmpError, setBmpError ] = useState(false);
    const [ empError, setEmpError ] = useState(false);
    const [ authorityType, setAuthorityType ] = useState('CTC');
    
    const refBmpIn = useRef(null);
    const refEmpIn = useRef(null);

    const clicLabel = isTerminalWindow ? 'CLIC#*' : 'Yard or Industrial CLIC#';

    // if edit existing windows, populate tracks control and selection, and clicNums
    useEffect(() => {
        // Scenarios when trackOptions is empty:
        // #1: first entry to step 2 ( new or existing window)
        // #2: reentry to step 2 but 
        //     #2.1 not entering (LS, BMP, EMP), which not calling getTrackListByLSMP yet
        //     #2.2 changed window type to/from Terminal Window type, which reset trackOptions
        if (props.trackOptions.length === 0) {
            if (isTerminalWindow) {
                let trackList = Object.values(TRACK_TYPES);
                props.setTrackOptions(trackList.sort(trackTypeUtil.sortByTrackType));
                setSelectedTracks(props.data);
            } else {
                const { lineSegment, bmp, emp } = props.data;

                if (isValidLSMP(lineSegment, bmp, emp)) {
                    const lsmp = toLSMP(props.data);
    
                    getTrackListByLSMP(lsmp).then(resp => {
                        // populate tracks dropdown with the track list defined by the window LSMP
                        onTrackListSuccess(resp);
        
                        // set tracks dropdown value by the given window
                        setSelectedTracks(props.data);
                    }, onTrackListFailure);
                }
            }
        }
        else {
            // set tracks dropdown value by the given window
            setSelectedTracks(props.data);
        }
    }, []);

    /**
     * Requirement: only apply Switch Yes to CTC territories.
     * @description To simply the logic, identify the authority type based on window's BMP and track
     */
    useEffect(() => {        
        if (props.data.lineSegment && isValidNumber(props.data.bmp) && props.data.trackList.length > 0) {
            getAuthorityType(props.data).then(resp => {
                // AuthorityType can have values of CTC, CTC_OTHER, TWC, RL, TWC-BABS, NON-TWC, etc.
                // Only consider CTC and CTC_OTHER as CTC, where SwitchYes will force to apply.
                // and all others as NON-CTC, where SwitchYes won't apply
                let arrAuthorityType_CTC = resp.authorityTypeDetails.filter(item => item.authorityType.includes('CTC'));
                
                setAuthorityType(arrAuthorityType_CTC.length > 0 ? 'CTC' : 'NON-CTC');
            }, (e) => {
                ToastsStore.showToast("AUTHORITY_TYPE_FAILURE");
            });
        }

    }, [props.data.bmp, props.data.bmpSuf, props.data.trackList]);

    useEffect(() => {
        const oSwitchYes = props.data.switchYes;

        if (oSwitchYes) {
            if (oSwitchYes.beginMilepost) {
                setBmp(oSwitchYes.beginMilepost.milepost + (oSwitchYes.beginMilepost.milepostSuffix || ''));
                setBcp(oSwitchYes.beginMilepost.controlPointName);
            }

            if (oSwitchYes.endMilepost) {
                setEmp(oSwitchYes.endMilepost.milepost + (oSwitchYes.endMilepost.milepostSuffix || ''));
                setEcp(oSwitchYes.endMilepost.controlPointName);
            }

            setSwitchYesResponse(oSwitchYes);
        }

    }, [ props.data.switchYes ]);

    /**
     * set tracks value by the input track window, and clic numbers if needed
     * @param {TrackWindow} win 
     * @precondition track list dropdown is populated
     * @note
     *  single-track window: 
     *      for Yard|Industry:
     *          selectedTrackItems: by win.trackType
     *          clicNums: by win.trackNum
     *      for not [Yard, Industry]:
     *          selectedTrackItems: win.trackType + win.trackNum 
     *          clicNums: ''
     *  multi-track window: by win.trackList
     *      for all Yard | Industry tracks, collect all their trackNums and put in clicNums
     */
    const setSelectedTracks = (win) => {
        let trackList = [],
            yardOrIndustry = null,
            arrClicNums = [];

        // multi-track window
        if (props.data.trackList.length > 0) {
            let fullTrackList = trackTypeUtil.toName_fromTracks(props.data.trackList);

            if (isTerminalWindow) {
                fullTrackList.forEach(trk => {
                    const [trkName, trkNum] = trk.split(TRACK_DELIMITER_REGEX);

                    if (!trackList.includes(trkName)) {
                        trackList.push(trkName);
                    }

                    arrClicNums.push(trkNum);
                });
            } else {
                trackList = fullTrackList.filter(trk => {
                    const [trkName, trkNum] = trk.split(TRACK_DELIMITER_REGEX);
    
                    if (ARRAY_YARD_INDUSTRY.includes(trkName)) {
                        yardOrIndustry = trkName;
                        arrClicNums.push(trkNum);
    
                        return false;
                    }
    
                    return true;
                });
    
                yardOrIndustry && trackList.push(yardOrIndustry);    
            }
            
            props.setSelectedTrackItems(fromArray(trackList));
            props.setClicNums(arrClicNums.join(', '));
        }
        // single-track window
        else {
            let trackName = trackTypeUtil.toName(props.data.trackType);

            if (trackName) {
                if (ARRAY_YARD_INDUSTRY.includes(trackName) || isTerminalWindow) {
                    trackList.push(trackName);
                    props.setClicNums(props.data.trackNum);
                } else {
                    trackList.push([trackName, props.data.trackNum].join(' '));
                }
    
                props.setSelectedTrackItems(fromArray(trackList));
            }
        }
    };

    const resetCP_Track_Clic = () => {
        if (bcp || ecp || props.trackOptions || props.clicNums) {
            setBcp('');
            setEcp('');
            !isTerminalWindow && props.setTrackOptions([]);
            props.setSelectedTrackItems([]);
            props.setClicNums('');
        }
    }

    const onChange_trackList = (selected) => {
        isTerminalWindow ? processTrackSelection_TW(selected) : processTrackSelection_nonTW(selected);
    }

    const processTrackSelection_nonTW = selected => {
        const selectedTracks = selected.map(x => x.value);            

        // selectedTracks: one instance ["Main 1", "Siding 1234", "Yard"]
        // codifiedTracks: ["M-1", "5-1234", "6"]
        const codifiedTracks = trackTypeUtil.toCode_fromTracks(selectedTracks);

        if (codifiedTracks.length === 1) {      // single-track selected
            let [trackTypeCode, trackNum] = codifiedTracks[0].split(TRACK_DELIMITER_REGEX);

            // '6' - Yard; 'I' - Industry
            // if not yard or industry selected,
            if (!(['6','I'].includes(trackTypeCode))) {
                props.updateWin({ 
                    trackType: trackTypeCode,
                    trackNum: trackNum,
                    trackList: [codifiedTracks[0]]
                }); 
            }
            // if yard or industry, handle inside onChange_clicNums since it may be a multi-track window
        } else if (codifiedTracks.length > 1) {     // multi-track selected
            props.updateWin({ 
                trackType: 'M',
                trackNum: '9',
                trackList: codifiedTracks.sort()
            });
        } else {    // no track selected
            props.updateWin({ 
                trackType: '',
                trackNum: '',
                trackList: []
            });
        }
        
        props.setSelectedTrackItems(selected);
    }

    const processTrackSelection_TW = selected => {
        props.setSelectedTrackItems([selected]);
    }

    const onChange_clicNums = (evt) => {
        props.setClicNums(evt.target.value);

        let regex = /^[0-9]+(,\s*[0-9]*)*$/;
        if (evt.target.value.length === 0 || regex.test(evt.target.value)) {
            evt.target.classList.remove('bwwp-invalid');
        } else {
            evt.target.classList.add('bwwp-invalid');
        }
    }

    const onBlur_clicNums = () => {
        let clics = props.clicNums.replace(/\s+/g, '').split(',');
        let selectedTracks = props.selectedTrackItems.map(x => x.value);

        // single-track window
        if (selectedTracks.length === 1 && clics.length === 1) {
            let trkType = trackTypeUtil.toCode(selectedTracks[0]),
                trkNum = clics[0];

            props.updateWin({ 
                trackType: trkType,
                trackNum: trkNum,
                trackList: [trkType + '-' + trkNum]
            });
        } 
        // multi-track window
        else if (selectedTracks.length > 1 || clics.length > 1) {
            let trackList = [];

            if (isTerminalWindow) {
                let trackCode = trackTypeUtil.toCode(props.selectedTrackItems[0].value);

                // add Yard and Industry tracks with their clic#
                clics
                    .map(clic => trackCode + '-' + clic)
                    .forEach(aTrack => trackList.push(aTrack));

                // update trackList with Yard and Industry tracks
                props.updateWin({ 
                    trackType: 'M',
                    trackNum: '9',
                    trackList: trackList.sort() 
                });
            }
            else {
                let yardOrIndustry = selectedTracks.includes('Yard') ? 'Yard' : 
                selectedTracks.includes('Industry') ? 'Industry' : null;

                if (yardOrIndustry) {
                    // remove Yard or Industry from trackList
                    let codifiedYardIndustry = trackTypeUtil.toCode(yardOrIndustry);
                    trackList = props.data.trackList.filter(trk => !trk.includes(codifiedYardIndustry));

                    // add Yard and Industry tracks with their clic#
                    clics
                        .map(clic => codifiedYardIndustry + '-' + clic)
                        .forEach(yiTrack => trackList.push(yiTrack));

                    // update trackList with Yard and Industry tracks
                    props.updateWin({ 
                        trackType: 'M',
                        trackNum: '9',
                        trackList: trackList.sort() 
                    });
                }
            }
        }
    }

    const onChange_lineSegment = (evt) => {
        let newValue = evt.target.value;
        setLineSegment(newValue);
    }

    const onBlur_lineSegment = (evt) => {
        let newValue = evt.target.value;

        if (newValue !== props.data.lineSegment) {
            // reset all other location related controls
            setBmp('');
            setEmp('');
            resetCP_Track_Clic();

            props.updateWin({ 
                lineSegment: newValue,
                bmp: '',
                emp: '',
                trackList: []
            });
        }
    }

    const onChange_bmp = (evt) => {
        setBmp(evt.target.value);
        if (emp != undefined && emp != null && evt.target.value !== emp) {
            evt.target.classList.remove('bwwp-invalid');
            refEmpIn.current.classList.remove('bwwp-invalid');
            setBmpError(false);
            setEmpError(false);
        } else {
            evt.target.classList.add('bwwp-invalid');
            refEmpIn.current.classList.remove('bwwp-invalid');
            setBmpError(true);
        }
    }

    const onChange_emp = (evt) => {
        setEmp(evt.target.value);
        if (bmp != undefined && bmp != null && evt.target.value !== bmp) {
            evt.target.classList.remove('bwwp-invalid');
            refBmpIn.current.classList.remove('bwwp-invalid');
            setEmpError(false);
            setBmpError(false);
        } else {
            evt.target.classList.add('bwwp-invalid');
            refBmpIn.current.classList.remove('bwwp-invalid');
            setEmpError(true);
        }
    }

    const onBlur_bmp = (evt) => {
        let newValue = evt.target.value;
        const [mp, mpSuf] = [...newValue.matchAll(mp_regexp)];
        props.updateWin({ bmp: mp ? mp[0] : '', bmpSuf: mpSuf ? mpSuf[0] : '' });

        onChange_LSMP();
    }

    const onBlur_emp = (evt) => {
        let newValue = evt.target.value;

        const [mp, mpSuf] = [...newValue.matchAll(mp_regexp)];
        props.updateWin({ emp: mp ? mp[0] : '', empSuf: mpSuf ? mpSuf[0] : '' });

        onChange_LSMP();
    }

    const onChange_bcp = (evt) => {
        let newValue = evt.target.value;
        setBcp(newValue);

        props.updateWin({ bcp: newValue });
    }

    const onChange_ecp = (evt) => {
        let newValue = evt.target.value;
        setEcp(newValue);

        props.updateWin({ ecp: newValue });
    }

    const onChange_LSMP = () => {
        if (!isTerminalWindow && isValidLSMP(lineSegment, bmp, emp)) {
            resetCP_Track_Clic();

            // call service to get the control points and track list
            const lsmp = toLSMP(props.data);

            getControlPointsByLSMP(lsmp).then(onCPSuccess, onCPFailure);
            getTrackListByLSMP(lsmp).then(onTrackListSuccess, onTrackListFailure);
        } 
    }

    const onCPSuccess = (cpResponse) => {
        const arrCPs = cpResponse.controlPoints;
        const bgnCP = arrCPs && arrCPs[0] ? arrCPs[0].controlPointName : "";
        const endCP = arrCPs && arrCPs[1] ? arrCPs[1].controlPointName : "";

        setBcp(bgnCP);
        setEcp(endCP);

        props.updateWin({ bcp: bgnCP, ecp: endCP });
    }

    // For yard/industrial tracks, strip out the clic#, and only display INDUSTRY and/or YARD in the track list
    // User will have to enter CLIC# for industrial or yard tracks in the CLIC# input box if INDUSTRY or YARD
    // is selected
    const onTrackListSuccess = (response) => {
        //  1. preprocessing the list: filter, sort
        //  2. populate track type control with the list
        if (response.returnCode === '0') {
            let arrYardIndustry = [];

            let svcTrackList = response.trackInfoList.map(x => x.trackText);
            let standardTrackList = trackTypeUtil.standardizeNames(svcTrackList);
            let trackList = standardTrackList.filter((track, idx) => {
                const [trackName, ] = track.split(TRACK_DELIMITER_REGEX);

                if (ARRAY_YARD_INDUSTRY.includes(trackName)) {
                    if (!arrYardIndustry.includes(trackName)) {
                        arrYardIndustry.push(trackName);
                    }

                    return false;
                }

                return standardTrackList.indexOf(track) === idx;
            });
            
            trackList = trackList.concat(arrYardIndustry);
            props.setTrackOptions(trackList.sort(trackTypeUtil.sortByTrackType));
        }
        else {
            props.setTrackOptions([]);
            ToastsStore.showToast("TRACKLIST_FAILURE");
        }
    }

    const onCPFailure = () => ToastsStore.showToast("CONTROLPOINT_FAILURE");
    const onTrackListFailure = () => ToastsStore.showToast("TRACKLIST_FAILURE");
    const onCancel = () => props.onCancel();
    const goBack = () => props.onBack();
    const goNext = () => props.onNext();    

    const clicNums_disabled = () => {
        let disabled = false;
        let selectedTracks = props.selectedTrackItems.map(x => x.value);

        if (isTerminalWindow) {
            disabled = !selectedTracks.length;
        } else {
            disabled = !(selectedTracks.includes('Yard') || selectedTracks.includes('Industry'));
        }

        return disabled;
    }

    const canNext = () => {
        let canProceed = isValidLSMP(lineSegment, bmp, emp) && props.selectedTrackItems.length > 0;
        const includeYardOrIndustry = props.selectedTrackItems.some(item => ARRAY_YARD_INDUSTRY.includes(item.value));
        if ((includeYardOrIndustry || isTerminalWindow) && !props.clicNums) {
            canProceed = false; 
        }

        document.getElementsByClassName('bwwp-invalid').length > 0 ? canProceed = false : canProceed ;
        return canProceed;
    }

    const canSwitchYes = () => {
        const validInput = isValidLSMP(lineSegment, bmp, emp) && props.selectedTrackItems.length > 0 && bcp.trim() && ecp.trim();

        return validInput && !switchYesInProgress;
    }

    const onSwitchYes = () => {
        setSwitchYesInProgress(true);

        applySwitchYes(props.data).then((resp) => {            
            setSwitchYesResponse(resp);
            setShowSwitchYesModal(true);
        }, onSwitchYes_failure)
        .finally(() => setSwitchYesInProgress(false));
    }

    const onSwitchYes_failure = (err) => {
        console.error(err);
        ToastsStore.showToast("SWITCH_YES_FAILURE");
    }

    /**
     * Apply switch yes selection
     * Do NOT change bmp and emp in props.data, but to add switchYes object to props.data
     * This design will enable us easily undo switch yes result after applied. Will update
     * bmp and emp with switchYes result when calling services to validate and save changes.
     */
    const switchYesModal_onApply = () => {
        setShowSwitchYesModal(false);

        props.updateWin({
            switchYes: switchYesResponse
        });
    }

    // undo switch yes logic
    const onReset = () => {
        setBmp(props.data.bmp + props.data.bmpSuf);
        setEmp(props.data.emp + props.data.empSuf);
        setBcp(props.data.bcp);
        setEcp(props.data.ecp);

        props.updateWin({
            switchYes: null
        });
    }

    const switchYesModal_onCancel = () => {
        setShowSwitchYesModal(false);
    }

    // const bcpSwitchYes_onChange = (evt) => {
    //     setBmp(evt.target.checked ? props.data.switchYes.bcp.switchYesMP : props.data.switchYes.bcp.switchNoMP);
    // }

    // const ecpSwitchYes_onChange = (evt) => {
    //     setEmp(evt.target.checked ? props.data.switchYes.ecp.switchYesMP : props.data.switchYes.ecp.switchNoMP);
    // }

    /**
     * check if bmp/emp and/or track selections changed,
     * this is mainly for window edit scenario
     * @returns Boolean
     */
    const isDirty = () => {
        const origWin = props.origData;

        return (bmp.toUpperCase() !== origWin.bmp + origWin.bmpSuf.toUpperCase()) ||
            (emp.toUpperCase() !== origWin.emp + origWin.empSuf.toUpperCase()) ||
            (props.data.trackList.join('/') !== origWin.trackList.join('/'));
    }

    /**
     * Consider four scenarios
     *  1. false if a Terminal Window
     *  2. New Window: true when switchYesApplied = false; otherwise, false
     *  3. Window Edit: true when isDirty() = true; otherwise, false
     *      NOTE: under window edit if props.origData.lineSegment contains value
     *  4. false for the window is in a non-CTC territory
     * @returns Boolean true to display Switch Yes button
     * @description When edit an existing window, do NOT force SwitchYes logic if
     * no change to LSMP and tracks.
     */
    const showSwitchYesButton = () => {
        if (isTerminalWindow || switchYesApplied || (props.origData.lineSegment && !isDirty()) || authorityType !== 'CTC') {
            return false;
        }

        return true;
    }

    const switchYesApplied = !!props.data.switchYes;
    const bcpSwithYes = (switchYesResponse?.beginMilepost) ?
            ((switchYesResponse.beginMilepost.switchYes) ? 'Switch Yes' : 'Switch No')
            : '',
        ecpSwithYes = (switchYesResponse?.endMilepost) ?
            ((switchYesResponse.endMilepost.switchYes) ? 'Switch Yes' : 'Switch No')
            : '';

    // // disable CP checkbox if either its switchYes or switchNo is null returned from switchYes service
    // const bcpCheckboxDisabled = !(switchYesResponse?.beginMilepost.switchYes) || !(switchYesResponse?.beginMilepost.switchNo),
    //     ecpCheckboxDisabled = !(switchYesResponse?.endMilepost.switchYes) || !(switchYesResponse?.endMilepost.switchNo);

    return (
        <div className='clsWinEditStep clsWinEditStep2'>
            <div className='clsWinEditStep-block'>
                <div className='clsFlex'>
                    <div className='clsWinEditStep-ctrl-label plr-0 col-md-4'>LS*</div>
                    <div className='clsWinEditStep-ctrl-label plr-0 col-md-4'>BMP*</div>
                    <div className='clsWinEditStep-ctrl-label plr-0 col-md-4'>EMP*</div>
                </div>

                <div className='clsFlex'>
                    <input type="number" className="form-control" value={ lineSegment } onChange={ onChange_lineSegment } onBlur={ onBlur_lineSegment } disabled={ switchYesApplied } />
                    <input ref={ refBmpIn } type="text" className="form-control" value={ bmp } onChange={ onChange_bmp } onBlur={ onBlur_bmp }  disabled={ switchYesApplied } />
                    <input ref={ refEmpIn } type="text" className="form-control" value={ emp } onChange={ onChange_emp } onBlur={ onBlur_emp }  disabled={ switchYesApplied } />
                </div>

                {(()=> {
                    if(bmpError){
                       return (
                         <div className="clsFlex">
                           <span className='clsError-Span'></span>
                           <span className='clsError-Span'>BMP can't be equal to EMP.</span>
                           <span className='clsError-Span'></span>
                         </div>
                       );
                    } else if(empError){
                        return (
                          <div className="clsFlex">
                            <span className='clsError-Span'></span>
                            <span className='clsError-Span'></span>
                            <span className='clsError-Span'>EMP can't be equal to BMP.</span>
                          </div>
                        );
                    }
                })()}
            </div>
            <div className='clsWinEditStep-block'>
                <div className='clsFlex'>
                    <div className='clsWinEditStep-ctrl-label plr-0 col-md-6'>BCP</div>
                    <div className='clsWinEditStep-ctrl-label plr-0 col-md-6'>ECP</div>
                </div>
                <div className='clsFlex'>
                    <input type="text" className="form-control col-md-6" value={ bcp } onChange={ onChange_bcp } />
                    <input type="text" className="form-control col-md-6" value={ ecp } onChange={ onChange_ecp } />
                </div>
                { switchYesApplied && (bcpSwithYes || ecpSwithYes)?
                    <div className='clsFlex'>
                        <span className='clsCP-switch-yes col-md-6'>{ bcpSwithYes }</span>
                        <span className='clsCP-switch-yes col-md-6'>{ ecpSwithYes }</span>
                    </div>
                : null }
            </div>

            <div className='clsWinEditStep-block'>
                <div className='clsWinEditStep-ctrl-label'>Tracks*</div>
                <Select className='clsSelect-TrackList' multi = { !isTerminalWindow }
                            name = 'trackList'
                            options = { fromArray(props.trackOptions) } 
                            noOptionsMessage = { () => 'No Result Found' } 
                            disabled = { switchYesApplied }
                            optionClassName = 'clsTrackList_option'
                            value = { isTerminalWindow ? props.selectedTrackItems[0] : props.selectedTrackItems } 
                            onChange = { onChange_trackList } />
            </div>
            <div className='clsWinEditStep-block'>
                <div className='clsWinEditStep-ctrl-label'>{ clicLabel }</div>
                <input type="text" className="form-control" disabled={ clicNums_disabled() }
                            ref={ refClicNums }
                            placeholder='Comma-separated list'
                            value = { props.clicNums } 
                            onChange = { onChange_clicNums } 
                            onBlur = { onBlur_clicNums } />
                <span className='invalid-message'>Invalid input. Please use comma to separate clic numbers.</span>
            </div>
            <div className='clsWinEditStep-block'>
                <div className='clsWinEditStep-note'>*: Required field</div>
            </div>

            <div className='clsWinEditStep-block'>
                <button className='btn btn-bwwp-secondary' onClick={ onCancel }>CANCEL</button>

                { showSwitchYesButton() ?
                    <button className='btn btn-raised btn-bwwp-primary' disabled={ !canSwitchYes() } onClick={ onSwitchYes }>SWITCH YES/NO</button>
                    :
                    <Fragment>
                        { switchYesApplied ? <button className='btn btn-bwwp-secondary' onClick={ onReset }>RESET</button> : null }
                        <button className='btn btn-raised btn-bwwp-primary' disabled={ !canNext() } onClick={ goNext }>NEXT</button>
                    </Fragment>
                }

                <button className='btn btn-raised btn-bwwp-secondary' onClick={ goBack }>BACK</button>
            </div>

            { showSwitchYesModal ? <SwitchYesModal
                data={ switchYesResponse }
                onApply={ switchYesModal_onApply }
                onCancel={ switchYesModal_onCancel } />
            : null }
        </div>
    );
};

const mapStateToProps = state => {
    return {
        trackOptions: state.windowEdit.trackOptions,
        selectedTrackItems: state.windowEdit.selectedTrackItems,
        clicNums: state.windowEdit.clicNums
    };
}

const mapDispatchToProps = dispatch => {
    return {
        setTrackOptions: (trkOps) => dispatch({ type: ActionTypes.SET_TRACK_OPTIONS, trackOptions: trkOps }),
        setSelectedTrackItems: (trkItems) => dispatch({ type: ActionTypes.SET_SELECTED_TRACK_ITEMS, selectedTrackItems: trkItems }),
        setClicNums: (clics) => dispatch({ type: ActionTypes.SET_CLIC_NUMS, clicNums: clics })
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(WinEditStep2);